import {
  Button,
  Heading,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';

export type DocGenState =
  | { status: 'preparing'; title: string }
  | { status: 'error'; title: string; message?: string };

export type DocumentGeneratingTabContentProps = {
  state: DocGenState;
  onStop: () => void;
  onRetry: () => void;
};

/**
 * Full-tab "Preparing your document" view shown while a markdown document is
 * generated — the markdown counterpart to PdfFillTabContent's preparing state,
 * so both generation paths share one experience.
 */
export const DocumentGeneratingTabContent: React.FC<
  DocumentGeneratingTabContentProps
> = ({ state, onStop, onRetry }) => {
  if (state.status === 'error') {
    return (
      <Stack fullHeight justify="center" align="center" gap={4}>
        <Stack align="center" gap={4}>
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-bg-tertiary">
            <Icon
              name="AlertCircle"
              size="2xl"
              className="text-text-negative"
            />
          </div>
          <Stack align="center" gap={2}>
            <Heading size="xl">
              Something went wrong generating this document.
            </Heading>
            {state.message && (
              <Text color="secondary" className="text-center">
                {state.message}
              </Text>
            )}
          </Stack>
          <Stack align="center" gap={2}>
            <Button onClick={onRetry}>
              <Icon name="RotateCcw" size="auto" />
              Try again
            </Button>
            <Button variant="ghost" size="sm" onClick={onStop}>
              Close
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack fullHeight justify="center" align="center" gap={4}>
      <Stack align="center" gap={4}>
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-bg-tertiary">
          <Icon name="FileText" size="2xl" className="text-text-secondary" />
        </div>
        <Stack align="center" gap={2}>
          <Heading size="xl">Preparing your document</Heading>
          <Text color="secondary" className="text-center">
            This document will be generated from your session data.
          </Text>
          <Text size="sm" color="tertiary" className="text-center">
            You can switch tabs in the meantime — we'll keep working in the
            background.
          </Text>
        </Stack>
        <Stack align="center" gap={2}>
          <Button disabled>
            <LoadingIcon />
            Generating…
          </Button>
          <Button variant="ghost" size="sm" onClick={onStop}>
            Stop generating
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
